import { getGroupedUserEventOptions } from '../utils';
import config from '../../../../config';
import {
  USER_EVENT_STATUSES,
  USER_EVENT_ACTIVITY,
  EVENT_TYPE_MACH,
  EVENT_TYPE_DTMP,
  EVENT_TYPE_USER,
} from '../../../../constants';

const buildEventTypeDependecies = (
  eventTypes,
  allUsers,
  allTags,
  availableIotEventTypes,
  availableUserEventTypes,
  filterParameters,
  { dtmpAlgorithmTypes = [], pointGroups = [] } = {}
) => {
  const groupedOptions = getGroupedUserEventOptions(availableUserEventTypes);
  switch (eventTypes) {
    case EVENT_TYPE_MACH: {
      const remoteSourceSchema =
        pointGroups.length > 0
          ? {
              remoteSource: {
                default: filterParameters?.mach?.remoteSource,
                title: 'Remote Source',
                isMulti: true,
                uniqueItems: true,
                type: 'array',
                items: {
                  type: 'string',
                  anyOf: pointGroups.map((pg) => ({
                    enum: [pg],
                    title: pg,
                  })),
                },
              },
            }
          : {};

      const eventTypesSchema =
        (availableIotEventTypes?.mach || []).length > 0
          ? {
              eventTypes: {
                default: filterParameters?.mach?.eventType,
                title: 'Types',
                isMulti: true,
                uniqueItems: true,
                type: 'array',
                items: {
                  type: 'string',
                  anyOf: (availableIotEventTypes?.mach || []).map((eventType) => ({
                    enum: [eventType.id],
                    title: eventType.name,
                  })),
                },
              },
            }
          : {};

      return {
        eventType: {
          properties: {
            ...remoteSourceSchema,
            ...eventTypesSchema,
            active: {
              default: true,
              title: 'Hide inactive events',
              type: 'boolean',
            },
          },
        },
      };
    }
    case EVENT_TYPE_DTMP: {
      const dtmpEventTypesSchema =
        (availableIotEventTypes?.dtmp?.eventTypes || []).length > 0
          ? {
              eventTypes: {
                default: filterParameters?.dtmp?.eventType,
                title: 'Types',
                isMulti: true,
                uniqueItems: true,
                type: 'array',
                items: {
                  type: 'string',
                  anyOf: (availableIotEventTypes?.dtmp?.eventTypes || []).map((eventType) => ({
                    enum: [eventType.id],
                    title: eventType.name,
                  })),
                },
              },
            }
          : {};

      const algorithmTypeSchema =
        dtmpAlgorithmTypes.length > 0
          ? {
              algorithmType: {
                default: filterParameters?.dtmp?.algorithmType,
                title: 'Algorithm Type',
                isMulti: true,
                uniqueItems: true,
                type: 'array',
                items: {
                  type: 'string',
                  anyOf: dtmpAlgorithmTypes.map((type) => ({
                    enum: [type],
                    title: type,
                  })),
                },
              },
            }
          : {};

      return {
        eventType: {
          properties: {
            ...algorithmTypeSchema,
            ...dtmpEventTypesSchema,
          },
        },
      };
    }
    case EVENT_TYPE_USER: {
      const assignedToSchema =
        allUsers.length > 0 && !config.onPremise
          ? {
              assignedTo: {
                default: filterParameters?.user?.assignee,
                title: 'Assignee',
                type: 'array',
                uniqueItems: true,
                isMulti: true,
                items: {
                  type: 'string',
                  anyOf: allUsers.map((user) => ({
                    enum: [user.id],
                    title: user.name,
                  })),
                },
              },
            }
          : {};

      const usersSchema =
        allUsers.length > 0 && !config.onPremise
          ? {
              users: {
                default: filterParameters?.user?.creator,
                title: 'Creator',
                type: 'array',
                uniqueItems: true,
                isMulti: true,
                items: {
                  type: 'string',
                  anyOf: allUsers.map((user) => ({
                    enum: [user.id],
                    title: user.name,
                  })),
                },
              },
            }
          : {};

      const userTypesSchema =
        availableUserEventTypes.length > 0
          ? {
              userTypes: {
                default: filterParameters?.user?.type,
                title: 'Types',
                type: 'array',
                uniqueItems: true,
                isMulti: true,
                groupedOptions,
                items: {
                  type: 'string',
                  anyOf: availableUserEventTypes.map((eventType) => ({
                    enum: [eventType.id],
                    title: eventType.name,
                  })),
                },
              },
            }
          : {};

      const tagsSchema =
        allTags.length > 0
          ? {
              tags: {
                default: filterParameters?.user?.tags,
                title: 'Tags',
                type: 'array',
                uniqueItems: true,
                isMulti: true,
                noOptionsMessage: 'No tags found',
                items: {
                  type: 'string',
                  anyOf: allTags.map((tag) => ({
                    enum: [tag.id],
                    title: tag.name,
                  })),
                },
              },
            }
          : {};

      return {
        eventType: {
          properties: {
            ...assignedToSchema,
            ...usersSchema,
            ...userTypesSchema,
            activity: {
              type: 'string',
              title: 'Activity',
              default: filterParameters?.user?.activity || 'all',
              anyOf: USER_EVENT_ACTIVITY.map((activity) => ({
                enum: [activity.toLowerCase()],
                title: activity,
              })),
            },
            status: {
              default: filterParameters?.user?.status || 'all',
              type: 'string',
              title: 'Status',
              anyOf: USER_EVENT_STATUSES.map((status) => ({
                enum: [status.toLowerCase()],
                title: status,
              })),
            },
            ...tagsSchema,
          },
        },
      };
    }
    default: {
      // Build user properties conditionally
      const userAssignedTo =
        allUsers.length > 0 && !config.onPremise
          ? {
              assignedTo: {
                default: filterParameters?.user?.assignee,
                title: 'Assignee',
                type: 'array',
                placeholder: 'All',
                uniqueItems: true,
                isMulti: true,
                items: {
                  type: 'string',
                  anyOf: allUsers.map((user) => ({
                    enum: [user.id],
                    title: user.name,
                  })),
                },
              },
            }
          : {};

      const userUsers =
        allUsers.length > 0 && !config.onPremise
          ? {
              users: {
                default: filterParameters?.user?.creator,
                title: 'Creator',
                type: 'array',
                placeholder: 'All',
                uniqueItems: true,
                isMulti: true,
                items: {
                  type: 'string',
                  anyOf: allUsers.map((user) => ({
                    enum: [user.id],
                    title: user.name,
                  })),
                },
              },
            }
          : {};

      const userUserTypes =
        availableUserEventTypes.length > 0
          ? {
              userTypes: {
                default: filterParameters?.user?.type,
                title: 'Types',
                type: 'array',
                placeholder: 'All',
                uniqueItems: true,
                isMulti: true,
                groupedOptions,
                items: {
                  type: 'string',
                  anyOf: availableUserEventTypes.map((eventType) => ({
                    enum: [eventType.id],
                    title: eventType.name,
                  })),
                },
              },
            }
          : {};

      const userTags =
        allTags.length > 0
          ? {
              tags: {
                default: filterParameters?.user?.tags,
                title: 'Tags',
                type: 'array',
                placeholder: 'All',
                uniqueItems: true,
                isMulti: true,
                noOptionsMessage: 'No tags found',
                items: {
                  type: 'string',
                  anyOf: allTags.map((tag) => ({
                    enum: [tag.id],
                    title: tag.name,
                  })),
                },
              },
            }
          : {};

      // Build mach properties conditionally
      const machTypes =
        (availableIotEventTypes?.mach || []).length > 0
          ? {
              machTypes: {
                default: filterParameters?.mach?.eventType,
                title: 'Types',
                isMulti: true,
                uniqueItems: true,
                type: 'array',
                placeholder: 'All',
                items: {
                  type: 'string',
                  anyOf: (availableIotEventTypes?.mach || []).map((eventType) => ({
                    enum: [eventType.id],
                    title: eventType.name,
                  })),
                },
              },
            }
          : {};

      // Build dtmp properties conditionally
      const dtmpTypes =
        (availableIotEventTypes?.dtmp?.eventTypes || []).length > 0
          ? {
              dtmpEventTypes: {
                default: filterParameters?.dtmp?.eventType,
                title: 'Types',
                isMulti: true,
                uniqueItems: true,
                type: 'array',
                placeholder: 'All',
                items: {
                  type: 'string',
                  anyOf: (availableIotEventTypes?.dtmp?.eventTypes || []).map((eventType) => ({
                    enum: [eventType.id],
                    title: eventType.name,
                  })),
                },
              },
            }
          : {};

      return {
        eventType: {
          oneOf: [
            {
              properties: {
                eventType: { enum: ['user'] },
                ...userAssignedTo,
                ...userUsers,
                ...userUserTypes,
                activity: {
                  type: 'string',
                  title: 'Activity',
                  default: filterParameters?.user?.activity || 'all',
                  anyOf: USER_EVENT_ACTIVITY.map((activity) => ({
                    enum: [activity.toLowerCase()],
                    title: activity,
                  })),
                },
                status: {
                  default: filterParameters?.user?.status || 'all',
                  type: 'string',
                  title: 'Status',
                  anyOf: USER_EVENT_STATUSES.map((status) => ({
                    enum: [status.toLowerCase()],
                    title: status,
                  })),
                },
                ...userTags,
              },
            },
            {
              properties: {
                eventType: { enum: ['mach'] },
                ...machTypes,
                active: {
                  default: true,
                  title: 'Hide inactive events',
                  type: 'boolean',
                },
              },
            },
            {
              properties: {
                eventType: { enum: ['dtmp'] },
                ...dtmpTypes,
              },
            },
          ],
        },
      };
    }
  }
};

const getEventSeriesExportSchema = (
  panelId,
  allUsers,
  allTags,
  eventTypes,
  availableIotEventTypes,
  availableUserEventTypes,
  { filterParameters, pickerId, dtmpAlgorithmTypes = [], pointGroups = [] } = {}
) => {
  const eventTypeDependecies = buildEventTypeDependecies(
    eventTypes,
    allUsers,
    allTags,
    availableIotEventTypes,
    availableUserEventTypes,
    filterParameters,
    { dtmpAlgorithmTypes, pointGroups }
  );

  const eventSpecificProps = eventTypeDependecies.eventType?.properties || {};

  const hasTags = allTags.length;
  const hasTasks = availableUserEventTypes.some((x) => x.task);

  const schema = {
    type: 'object',
    required: ['range', 'exportContent'],
    properties: {
      range: {
        title: 'Time Interval',
        default: null,
        panelId,
        pickerId,
      },
      exportContent: {
        type: 'string',
        title: 'Export Content',
        placeholder: 'Select content',
        oneOf:
          eventTypes === EVENT_TYPE_MACH || eventTypes === EVENT_TYPE_DTMP || !hasTasks
            ? [{ enum: ['events'], title: 'Events' }]
            : [
                { enum: ['events'], title: 'Events' },
                { enum: ['statistics'], title: 'Statistics' },
              ],
      },
    },
    dependencies: {
      exportContent: {
        oneOf: [
          {
            properties: {
              exportContent: { enum: ['events'] },
              exportType: {
                type: 'string',
                title: 'Export format',
                default: 'xlsx',
                oneOf: [
                  { enum: ['xml'], title: '.xml' },
                  { enum: ['xlsx'], title: '.xlsx' },
                ],
              },
              ...eventSpecificProps,
            },
          },
          ...(hasTasks
            ? [
                {
                  properties: {
                    exportContent: { enum: ['statistics'] },
                    taskTypes: {
                      default: filterParameters?.user?.type,
                      title: 'Task Types',
                      type: 'array',
                      uniqueItems: true,
                      isMulti: true,
                      items: {
                        type: 'string',
                        anyOf: availableUserEventTypes
                          .filter((x) => x.task)
                          .map((eventType) => ({
                            enum: [eventType.id],
                            title: eventType.name,
                          })),
                      },
                    },
                  },
                },
              ]
            : []),
        ],
      },
    },
  };

  // Remove tags if not available
  if (!hasTags && eventSpecificProps.tags) {
    delete schema.dependencies.exportContent.oneOf[0].properties.tags;
  }

  return schema;
};

const getUiSchema = () => {
  return {
    range: {
      'ui:field': 'dateRangePicker',
    },
  };
};
export { getUiSchema, getEventSeriesExportSchema };
